/**
 * Company: SPHMedia
 * Description: [myBH following topic page article card]
 */

import { LabelCardIconVariety } from "@elements/Icon/LabelCardIcon";
import LabelCardIconNew from "@elements/Icon/LabelCardIconNew";
import { Image } from "@elements/ResponsiveImage/Image";
import Date from "@elements/Typography/Date";
import getFallbackSectionImage from "@helper/getFallbackSectionImage";
import {
  findStyleBySectionAndSubCategory,
  findStyleBySectionNew,
  isArrayWithElements,
} from "@helper/utils";
import { Heading } from "@sphtech/sph-web-ui/elements/Heading";
import { RawImageField } from "@typings/Opensearch";
import { ReactElement } from "react";
import { Link } from "react-router";

type ImageProps = {
  imageAlt: string;
  imageField?: RawImageField;
};

type SectionDetailsProps = {
  linkToSection: string;
  parentSection?: string;
  sectionName?: string;
  uniqueSectionName?: string;
};

export type MyBHArticleCardProps = {
  title: string;
  articleUrlPath: string;
  image: ImageProps;
  sectionDetails: SectionDetailsProps;
  date: string;
  time: string;
  contentAccess?: LabelCardIconVariety;
  paragraph?: string;
  tags?: {
    alias: string;
    name: string;
  }[];
  authors?: {
    name: string;
    urlPath: string;
  }[];
};

/**
 * MyBHArticleCard
 * Description: [Article card for myBH following topic page]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function MyBHArticleCard(
  props: MyBHArticleCardProps,
): ReactElement {
  const {
    title,
    articleUrlPath,
    image,
    sectionDetails,
    date,
    time,
    contentAccess,
    paragraph,
    tags,
    authors,
  } = props;

  const { imageAlt, imageField } = image;

  const { sectionName, parentSection, uniqueSectionName, linkToSection } =
    sectionDetails;

  const dummyImage = getFallbackSectionImage(sectionName);

  const { selectedColor } = parentSection
    ? findStyleBySectionAndSubCategory(parentSection) || {}
    : findStyleBySectionNew(uniqueSectionName) || {};
  const textColor = selectedColor ? `text-${selectedColor}` : "";

  return (
    <div
      data-testid="myBHArticleCard-wrapper"
      className="flex flex-col md:flex-row gap-3 md:gap-4 pb-4 md:pb-0"
    >
      <div
        data-testid="card-image"
        className="md:max-w-[179px] lg:max-w-[280px]"
      >
        <Link
          to={articleUrlPath}
          reloadDocument
          data-testid="link"
          className=""
        >
          <Image
            displayWidth={360}
            srcWidth={360}
            srcHeight={270}
            src={imageField?.url ?? dummyImage}
            alt={imageAlt}
            dataTestId="image"
            className="md:hidden"
          />
          <Image
            displayWidth={280}
            srcWidth={280}
            srcHeight={190}
            src={imageField?.url ?? dummyImage}
            alt={imageAlt}
            dataTestId="image"
            className="hidden md:block"
          />
        </Link>
      </div>
      <div
        data-testid="card-details"
        className="flex-grow-no-shrink flex flex-col gap-3 md:gap-2"
      >
        <div
          data-testid="card-section-label"
          className="flex justify-between items-center"
        >
          <span
            className={`text-xxs font-bold capitalize hover:underline text-nowrap ${textColor}`}
          >
            <Link to={linkToSection} reloadDocument>
              {sectionName}
            </Link>
          </span>
          {contentAccess && (
            <LabelCardIconNew className="" contentAccess={contentAccess} />
          )}
        </div>
        <div data-testid="card-title-para" className="flex flex-col gap-2">
          <Link to={articleUrlPath} reloadDocument>
            <Heading
              data-testid="title-story"
              className="text-text-body font-secondary font-semibold text-md md:text-base lg:text-[20px] leading-[120%]"
            >
              {title}
            </Heading>
          </Link>
          <span
            data-testid="paragraph"
            className="hidden lg:block text-neutral-700 leading-[150%] font-primary font-medium text-base"
          >
            {paragraph}
          </span>
        </div>
        <div
          data-testid="card-date-tags-author"
          className="flex justify-between items-end"
        >
          <div data-testid="card-date-author">
            <Date
              date={date}
              time={time}
              variant={"or"}
              className="text-[16px] md:text-[14px] text-text-muted font-primary font-medium md:font-regular"
            />
            {isArrayWithElements(authors) && (
              <div
                data-testid="authors"
                className="text-base md:text-xs font-primary font-regular leading-[150%] text-blue-100"
              >
                <Link to={authors[0].urlPath} reloadDocument>
                  {authors[0].name}
                </Link>
              </div>
            )}
          </div>
          {isArrayWithElements(tags) && (
            <div
              data-testid="card-tags"
              className="text-base md:text-xs font-primary font-regular leading-[150%] text-blue-100"
            >
              <Link to={tags[0].alias} reloadDocument>
                {tags[0].name}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
